import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import clsx from "clsx";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Component Imports
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import ActionBar from "../../../components/CRUDcomponents/Actionbar/ActionBar";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const L1Indent = () => {
  // State Management
  const [indents, setIndents] = useState([]);
  const [filteredIndents, setFilteredIndents] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const rowsPerPage = 10;
  

  const navigate = useNavigate();

  // Redux Auth State
  const authState = useSelector((state) => state.auth);
  const {
    role,
    locationid,
    isAuthenticated,
    isAuthorized,
  } = authState;
  const id = authState.id || localStorage.getItem("id");

  // Dynamic API Endpoint based on role
  const getApiEndpoint = useCallback(() => {
    console.log("Determining API endpoint for role:", role);
    return role === "admin"
      ? `${API_BASE_URL}/adminpage/l1indent`
      : `${API_BASE_URL}/l1page/indent`;
  }, [role]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredIndents.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentIndents = filteredIndents.slice(startIndex, endIndex);

  // Format Date Function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    // If the date is already in dd/MM/yy format, return it as is
    if (typeof dateString === 'string' && dateString.match(/^\d{2}\/\d{2}\/\d{2}$/)) {
      return dateString;
    }

    try {
    const options = { day: "2-digit", month: "2-digit", year: "numeric" };
      return new Intl.DateTimeFormat("en-GB", options).format(new Date(dateString));
    } catch (error) {
      console.error("Date formatting error:", error);
      return "N/A";
    }
  };

  // Fetch Indents
  const fetchIndents = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    console.log("Starting fetchIndents...");
    console.log("Current User ID:", id); // Debug log
  
    try {
      const endpoint = getApiEndpoint();
      console.log("Using endpoint:", endpoint);
  
      // Always include UserId in query params
      const queryParams = { UserId: id };
      console.log("Query params:", queryParams); // Debug log
      
      const response = await axios.get(endpoint, {
        params: queryParams,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
  
      console.log("API Response:", response.data); // Debug log
  
      const indentsData = Array.isArray(response.data)
        ? response.data
        : response.data.data || [];
  
      if (!indentsData.length) {
        console.warn("No indents found");
        setError("No indents found for this location");
        setIndents([]);
        setFilteredIndents([]);
        return;
      }
  
      // Format CreationDate field for each indent
      const formattedIndents = indentsData.map((indent) => ({
        ...indent,
        CreationDate: formatDate(indent.CreationDate),
      }));
  
      console.log("Formatted indents:", formattedIndents); // Debug log
      setIndents(formattedIndents);
      setFilteredIndents(formattedIndents);
      setCurrentPage(1);
    } catch (error) {
      console.error("Comprehensive Fetch Error:", error.response || error);
      setError(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch indents"
      );
      setIndents([]);
      setFilteredIndents([]);
    } finally {
      setIsLoading(false);
    }
  }, [id, getApiEndpoint]);

  // Fetch Data Effect
  useEffect(() => {
    const shouldFetchData =
      role &&
      (role === "admin" || role === "Location Head") &&
      isAuthenticated &&
      isAuthorized &&
      localStorage.getItem("token");

    if (shouldFetchData) {
      fetchIndents();
    } else {
      console.warn("Data Fetching Conditions Not Met", {
        roleCheck: !!role,
        roleType: role,
        authCheck: isAuthenticated,
        authorizedCheck: isAuthorized,
        tokenCheck: !!localStorage.getItem("token"),
      });
      setIsLoading(false);
    }
  }, [role, isAuthenticated, isAuthorized, fetchIndents]);

  // Handle Row Click
  const handleRowClick = (row) => {
    console.log("Navigating with row:", row);
    navigate(`/L1table`, {
      state: {
        indentId: row.IndentID,
        indentDetails: row,
        row: row,
      },
    });
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  // Search Handler
  const handleSearch = (searchTerm) => {
    setSearchQuery(searchTerm); // Update the local search state
    if (!searchTerm || searchTerm.trim() === '') {
      setFilteredIndents(indents);
      return;
    }
    
    const searchTermLower = searchTerm.toLowerCase().trim();
    const filtered = indents.filter((indent) => {
      const searchableFields = [
        indent.IndentID?.toString(),
        indent.ProjectName,
        indent.TypeName,
        indent.Status,
        indent.CreationDate,
        indent.TaskSchedule
      ];
      
      return searchableFields.some(field => 
        field && field.toString().toLowerCase().includes(searchTermLower)
      );
    });
    
    setFilteredIndents(filtered);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Date Filter Handler
  const handleDateFilter = (startDate, endDate) => {
    const filtered = indents.filter((indent) => {
      const creationDate = new Date(indent.CreationDate);
      return (
        (!startDate || creationDate >= new Date(startDate)) &&
        (!endDate || creationDate <= new Date(endDate))
      );
    });
    setFilteredIndents(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const getEditFields = () => [
    { key: "IndentID", label: "Indent ID" },
    { key: "ProjectName", label: "Project" },
    { key: "CreationDate", label: "Creation Date" },
    { key: "Status", label: "Status" },
  ];

  const columns = [
    { key: "IndentID", label: "Indent ID" },
    { key: "CreationDate", label: "Creation Date" },
    { key: "ProjectName", label: "Project" },
    { key: "TypeName", label: "Type" },
    { key: "TaskSchedule", label: "Task Schedule" },
    { key: "Status", label: "Status" },
    { key: "ItemCount", label: "Item Count" },
  ];

  const renderContent = () => {
    return (
      <div className="space-y-4">
        {/* ActionBar is now outside of any conditional checks */}
        <ActionBar
          title="Indent"
          fields={getEditFields()}
          apiEndpoint={getApiEndpoint()}
          data={filteredIndents}
          selectedRow={selectedRow}
          onRowSelect={setSelectedRow}
          onRefresh={fetchIndents}
          onSearch={handleSearch}
          onFilter={handleDateFilter}
          rowKey="IndentID"
          userRole={role}
          addLink="/addl1Indent"
          editRoute="/L1Table"
          searchQuery={searchQuery}
        />

        {isLoading ? (
          <div className="text-center p-4">
            <p>Loading indents...</p>
          </div>
        ) : error ? (
          <div className="text-center text-red-500 p-4">
            <p>Error: {error}</p>
          </div>
        ) : (
          <>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100 text-gray-800">
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className="border border-gray-300 px-6 py-3 text-left text-lg font-semibold"
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentIndents.map((row) => (
                    <tr
                      key={row.IndentID}
                      className={clsx(
                        "cursor-pointer",
                        selectedRow?.IndentID === row.IndentID ? "bg-blue-100" : "",
                        "hover:bg-gray-50",
                        "border-b border-gray-300"
                      )}
                      onClick={() => handleRowClick(row)}
                    >
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className="border border-gray-300 px-5 py-3 text-base"
                        >
                          {row[column.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {currentIndents.length === 0 && (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="border px-4 py-2 text-center text-gray-500"
                      >
                        No L1 indents found for this location
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filteredIndents.length > 0 && (
              <div className="flex items-center justify-between px-4 py-3">
                <div className="text-sm text-gray-700">
                  Showing {startIndex + 1} to{" "}
                  {Math.min(endIndex, filteredIndents.length)} of{" "}
                  {filteredIndents.length} entries
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={clsx(
                      "inline-flex items-center rounded px-3 py-2",
                      "border border-gray-300 bg-white text-sm font-medium",
                      "focus:outline-none focus:ring-2 focus:ring-blue-500",
                      currentPage === 1
                        ? "cursor-not-allowed text-gray-400"
                        : "text-gray-700 hover:bg-gray-50"
                    )}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="ml-1">Previous</span>
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={clsx(
                      "inline-flex items-center rounded px-3 py-2",
                      "border border-gray-300 bg-white text-sm font-medium",
                      "focus:outline-none focus:ring-2 focus:ring-blue-500",
                      currentPage === totalPages
                        ? "cursor-not-allowed text-gray-400"
                        : "text-gray-700 hover:bg-gray-50"
                    )}
                  >
                    <span className="mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-grow p-4">
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">
            {role === "admin" ? "L1 Indents" : "L1 Indents"}
          </h3>
        </div>
        {renderContent()}
      </div>
      <Footer />
    </div>
  );
};
export default L1Indent;
